import { Dialog } from "construct-ui";
import moment from "moment";
import NumberInputComponent from "../../components/number/number-input.component";
import paginationComponent from "../../components/pagination/pagination.component";
import SingleSelectComponent from "../../components/single-select/single-select.component";
import SelectSiteComponent from "../../components/site/select-site.component";
import { PURCHASE_ORDER_STATUS_CODE, formats } from "../../config/constants";
import codeMasterModel from "../../models/common/code-master.model";
import loginModel from "../../models/login.model";
import PurchaseOrderProposalModel from "../../models/purchase-order-proposal/purchase-order-proposal.model";
import ViewBase from "../view-base.view";
import PurchaseOrderProposalExcelUploadDialog from "./purchase-order-proposal.excel-upload.dialog";

/**
 * Purchase Order View
 * 
 * A page that displays a list of purchase order to approve / reject
 * 
 * @author Darrus Goh
 */
export default function PurchaseOrderProposalView() {
    ViewBase.call(this);

    const SELECT_ALL_CHECKBOX_ID = 'purchaseOrderSelectAllCheckbox';
    const CHECKBOX_NAME = 'purchaseOrderCheckbox';

    const DEFAULT_FILTERS = {
        week: moment().format(formats.WEEK_FORMAT),
        sites: [],
        models: [],
        status: ''
    };

    const DEFAULT_PAGINATION = {
        pageSize: 10,
        pageIndex: 1,
        totalItems: 0
    };


    let filters = structuredClone(DEFAULT_FILTERS);
    let pagination = structuredClone(DEFAULT_PAGINATION);
    let statuses = [];
    let statusMap = {};

    let purchaseOrders = [];
    let selectedPurchaseOrders = [];
    let unsuccessfulPurchaseOrders = [];

    let selectedAction = false; // Approve = true, Reject = false
    let isConfirmationDialogOpen = false;
    let isActionErrorDialogOpen = false;
    let isExcelUploadOpen = false;

    const getStatuses = () => {
        codeMasterModel.getByCategory(
            (result) => {
                statuses = result;
                statusMap = result.reduce((accu, item) => {
                    accu[item.code] = item.name;
                    return accu;
                }, {});
            },
            (error) => {
                this.handleError(error);
            },
            { category: 'POProposalStatus' }
        );
    };

    const downloadPurchaseOrders = () => {
        this.showLoading();
        PurchaseOrderProposalModel.downloadPurchaseOrders(toQueryParams(filters))
            .catch((error) => {
                this.handleError(error);
            })
            .finally(() => {
                this.hideLoading();
            });
    };

    const getPurchaseOrders = () => {
        this.showLoading();
        uncheckAllCheckboxes();
        PurchaseOrderProposalModel.getPurchaseOrders(toQueryParams(filters))
            .then((result) => {
                purchaseOrders = formatData(result.data);
                pagination.pageIndex = result.pageIndex;
                pagination.pageSize = result.pageSize;
                pagination.totalItems = result.total;
            })
            .catch((error) => {
                this.handleError(error);
            })
            .finally(() => {
                this.hideLoading();
            });
    };

    const toQueryParams = (params) => {
        return Object.assign(
            {
                pageSize: pagination.pageSize,
                pageIndex: pagination.pageIndex,
                week: moment(params.week, formats.WEEK_FORMAT).format('yyyyww'),
                channelCodes: !_.isEmpty(params.sites) ? params.sites.map((e) => e.code) : null,
                modelCodes: params.models?.length > 0 ? params.models : null,
                statusCode: params.status ? params.status.code : null
            },
        );
    };

    const formatData = (data) => {
        let result = structuredClone(data);

        result.forEach((e) => {
            // Save initial quantity to check for changes
            e.initialApprovedQty = e.approvedQty;

            // if no approved qty, assign proposed qty
            if (e.approvedStatus === PURCHASE_ORDER_STATUS_CODE.PENDING && !e.approvedQty) {
                e.approvedQty = e.proposedQty;
            }

            e.UID = e.week + e.channelCode + e.modelCode;
            e.siteDisplay = `${e.channelName} (${e.channelCode})`;
            e.weekDisplay = moment(e.week, 'ggggww').format('gg[W]ww');
            e.editableYN = e.editableYN === 'Y';
            e.approvedStatusName = statusMap[e.approvedStatus];
            e.approvedDate = e.approvedDate ? moment(e.approvedDate).format(formats.dateFormat) : null;
            e.submittedDate = e.createDate ? moment(e.createDate).format(formats.dateFormat) : null;
        });

        return result;
    };

    const saveChanges = () => {
        if (!hasChanges()) {
            return;
        }

        let filteredPurchaseOrders = purchaseOrders.filter((item) => item.approvedStatus === PURCHASE_ORDER_STATUS_CODE.PENDING && item.approvedQty !== item.initialApprovedQty);
        purchaseOrdersAction(filteredPurchaseOrders).then(() => {
            let unsuccessfulTemp = structuredClone(unsuccessfulPurchaseOrders);

            // Update existing values without refreshing table
            filteredPurchaseOrders.forEach((item) => {
                let unsuccessfulIndex = unsuccessfulTemp.findIndex((e) => e.UID = item.UID);
                if (unsuccessfulIndex < 0) {
                    item.initialApprovedQty = item.approvedQty;
                } else {
                    unsuccessfulTemp = unsuccessfulTemp.splice(unsuccessfulIndex, 1);
                }
            });

            if (unsuccessfulPurchaseOrders.length === 0) {
                this.showMessage(this.getText('EM00000762', 'Changes have been saved.'));
            }
        });
    };

    const onclickAction = (approve = false) => {
        if (selectedPurchaseOrders.length > 0) {
            selectedAction = approve;
            isConfirmationDialogOpen = true;
        } else {
            this.showErrorMessage(this.getText('LT00000719', 'No items selected'));
        }
    };

    const confirmationAction = (approve = false) => {
        if (selectedPurchaseOrders.length === 0) {
            return;
        }

        const approvedStatus = approve ? PURCHASE_ORDER_STATUS_CODE.APPROVED : PURCHASE_ORDER_STATUS_CODE.REJECTED;
        selectedPurchaseOrders.forEach((item) => {
            item.approvedStatus = approvedStatus;
        });

        purchaseOrdersAction(selectedPurchaseOrders)
            .then(() => {
                // Refresh table
                getPurchaseOrders();
            });
    };

    const purchaseOrdersAction = (items) => {
        this.showLoading();
        return PurchaseOrderProposalModel
            .purchaseOrdersAction({ proposalList: toActionParams(items) })
            .then((result) => {
                unsuccessfulPurchaseOrders = [];

                result.forEach((item) => {
                    // If item is unsuccessful
                    if (item.messageCode !== 'EM00000004') {
                        item.UID = item.week + item.channelCode + item.modelCode;
                        unsuccessfulPurchaseOrders.push(item);
                    }
                });

                if (unsuccessfulPurchaseOrders.length > 0) {
                    showActionErrorDialog(unsuccessfulPurchaseOrders);
                }
            })
            .catch((error) => {
                this.handleError(error);
            })
            .finally(() => {
                this.hideLoading();
            });
    };

    const toActionParams = (data) => {
        return data.map((item) => {
            let result = {};
            result.week = item.week;
            result.channelCode = item.channelCode;
            result.modelCode = item.modelCode;
            result.proposedQuantity = item.proposedQty;
            result.approvedQuantity = item.approvedQty;
            result.actionStatus = item.approvedStatus;
            return result;
        });
    };

    const showActionErrorDialog = (errorItems) => {
        errorItems.forEach((item) => {
            item.weekDisplay = moment(item.week, 'ggggww').format('gggg[W]ww');
        });
        isActionErrorDialogOpen = true;
    }

    const uncheckAllCheckboxes = () => {
        document.querySelectorAll(`input[name=${CHECKBOX_NAME}]`).forEach((checkbox) => {
            checkbox.checked = false;
        });
        let selectAllCheckbox = document.querySelector(`#${SELECT_ALL_CHECKBOX_ID}`);
        selectAllCheckbox.indeterminate = false;
        selectAllCheckbox.checked = false;

        selectedPurchaseOrders = [];
    }

    const clearFilter = () => {
        filters = structuredClone(DEFAULT_FILTERS);
        pagination = structuredClone(pagination);
        getPurchaseOrders();
    };

    const onCheckAll = (event) => {
        let checkboxes = document.querySelectorAll(`input[type='checkbox'][name='${CHECKBOX_NAME}']`);

        selectedPurchaseOrders = [];

        if (event.target.checked || event.target.indeterminate) {
            purchaseOrders.forEach((item) => {
                if (item.editableYN) {
                    selectedPurchaseOrders.push(item);
                }
            });

            checkboxes.forEach((item) => {
                item.checked = true;
            });
        } else {
            checkboxes.forEach((item) => {
                item.checked = false;
            });
        }
        event.target.indeterminate = false;
    };

    const selectRow = (checked, item) => {
        if (checked) {
            selectedPurchaseOrders.push(item);
        } else {
            selectedPurchaseOrders = selectedPurchaseOrders.filter((data) => data.UID !== item.UID);
        }

        let selectAllCheckbox = document.querySelector(`#${SELECT_ALL_CHECKBOX_ID}`);
        if (selectedPurchaseOrders.length === 0) {
            selectAllCheckbox.indeterminate = false;
            selectAllCheckbox.checked = false;
        }
        else if (selectedPurchaseOrders.length === purchaseOrders.filter((item) => item.editableYN).length) {
            selectAllCheckbox.indeterminate = false;
            selectAllCheckbox.checked = true;
        } else {
            selectAllCheckbox.indeterminate = true;
            selectAllCheckbox.checked = false;
        }
    };

    const hasChanges = () => {
        if (purchaseOrders.length === 0) {
            return false;
        }

        return purchaseOrders.some((e) => e.initialApprovedQty !== e.approvedQty);
    };

    const leavePageEventListener = (e) => {
        if (hasChanges() && !confirm(this.getText('EM00000776', 'Changes you made may not be saved'))) {
            e.preventDefault();
            e.stopImmediatePropagation();
        }
    };

    return {
        oncreate: () => {
            addEventListener('route', leavePageEventListener);
            addEventListener('beforeunload', leavePageEventListener);
        },
        onremove: () => {
            removeEventListener('route', leavePageEventListener);
            removeEventListener('beforeunload', leavePageEventListener);
        },
        oninit: () => {
            getStatuses();
        },
        view: () => {
            return [
                /* SEARCH */
                m('.card.mb-3',
                    m('.card-body',
                        m('.row.g-2',
                            m('.col-md-3.col-lg-3.position-relative',
                                m("label.form-label.mandatory", this.getText('LT00000337', 'Week')),
                                m("input.form-control.form-control-bg", {
                                    type: "week",
                                    value: filters.week,
                                    onchange: (e) => { filters.week = e.target.value }
                                })
                            ),
                            m('.col-md-3.col-lg-3.position-relative',
                                m('label.form-label', this.getText('LT00000071', 'Site')),
                                m(SelectSiteComponent, {
                                    selectedItemList: filters.sites,
                                    onChange: (s) => {
                                        filters.sites = s;
                                    },
                                    countryCode: loginModel.credentials.country
                                })
                            ),
                            m('.col-md-3.col-lg-3.position-relative',
                                m('label.form-label', this.getText('LT00000717', 'Model')),
                                m('input', {
                                    class: 'form-control form-control-bg',
                                    placeholder: this.getText('LT00000728', 'Model Codes'),
                                    type: 'text',
                                    value: filters.models,
                                    onchange: (e) => { filters.models = e.target.value.replace(/\s/g, '').split(','); }
                                }),
                                m('p.text-muted.fw-light.fst-italic.m-0.ps-1', this.getText('LT00000762', 'Supports multiple inputs separated by comma ( , )'))
                            ),
                            m('.col-md-3.col-lg-3.position-relative',
                                m('label.form-label', this.getText('LT00000015', 'Status')),
                                m(SingleSelectComponent, {
                                    items: statuses,
                                    selectedItem: filters.status,
                                    checkmark: true,
                                    filterable: false,
                                    onSelect: (s) => { filters.status = s }
                                })
                            ),
                            m('.col-12.d-flex.justify-content-end.gap-2',
                                m('button.btn.btn-primary',
                                    {
                                        onclick: () => {
                                            pagination = structuredClone(DEFAULT_PAGINATION);
                                            getPurchaseOrders();
                                        }
                                    },
                                    this.getText("LT00000001", "Search")
                                ),
                                m('button.btn.btn-white',
                                    {
                                        type: 'button',
                                        onclick: () => {
                                            clearFilter();
                                        }
                                    },
                                    this.getText("LT00000031", "Clear")
                                )
                            )
                        )
                    )
                ),


                /* TABLE */
                m('.card',
                    m('.card-body',
                        m('.row.g-2',
                            m('.col-12.d-flex.justify-content-between',
                                m('.d-flex.gap-2',
                                    m('button.btn.btn-primary', { onclick: () => { onclickAction(true) } }, this.getText('LT00000759', 'Approve Selected')),
                                    m('button.btn.btn-danger', { onclick: () => { onclickAction(false) } }, this.getText('LT00000760', 'Reject Selected')),
                                    m('button.btn.btn-primary', { onclick: saveChanges }, this.getText('LT00000022', 'Save'))
                                ),
                                m('.d-flex.gap-2',
                                    m('button.btn.btn-primary', { onclick: () => { isExcelUploadOpen = true; } }, m('i.bi.bi-file-earmark-arrow-up-fill'), this.getText('LT00000197', 'Excel Upload')),
                                    m('button.btn.btn-primary', { onclick: downloadPurchaseOrders }, m('i.bi.bi-file-earmark-arrow-down-fill'), this.getText('LT00000018', 'Excel Download'))
                                )
                            ),
                            m('.col-12',
                                m('.table-responsive',
                                    m('table.table.table-hover.sticky-column-start',
                                        m('thead',
                                            m('tr.align-middle',
                                                m('th',
                                                    m('input', {
                                                        tabindex: -1,
                                                        id: SELECT_ALL_CHECKBOX_ID,
                                                        disabled: purchaseOrders.filter((item) => item.editableYN).length === 0,
                                                        type: 'checkbox',
                                                        onchange: onCheckAll
                                                    })
                                                ),
                                                m('th', this.getText('LT00000337', 'Week')),
                                                m('th', this.getText('LT00000745', 'Master Dealer')),
                                                m('th', this.getText('LT00000746', 'EDI Client Code')),
                                                m('th', this.getText('LT00000072', 'Site Group')),
                                                m('th', this.getText('LT00000321', 'Site Code / Name')),
                                                m('th', this.getText('LT00000722', 'Region')),
                                                m('th', this.getText('LT00000747', 'Store Category')),
                                                m('th', this.getText('LT00000328', 'Marketing Name')),
                                                m('th', this.getText('LT00000748', 'Model Code')),
                                                m('th', this.getText('LT00000147', 'Color')),
                                                m('th', this.getText('LT00000749', 'Memory')),
                                                m('th', this.getText('LT00000750', 'Target WOS')),
                                                m('th', this.getText('LT00000751', 'P4W AVG S/O')),
                                                m('th', this.getText('LT00000752', 'Inventory')),
                                                m('th', this.getText('LT00000753', 'Proposed S/I QTY')),
                                                m('th', this.getText('LT00000754', 'Approved S/I QTY')),
                                                m('th', this.getText('LT00000015', 'Status')),
                                                m('th', this.getText('LT00000756', 'Approved by')),
                                                m('th', this.getText('LT00000755', 'Approved on')),
                                                m('th', this.getText('LT00000415', 'Submitted by')),
                                                m('th', this.getText('LT00000757', 'Submitted on'))
                                            )
                                        ),
                                        m('tbody',
                                            purchaseOrders.map((data, index) =>
                                                m(`tr.align-middle${data.editableYN ? '.cursor-pointer' : ''}`,
                                                    data.editableYN && {
                                                        onclick: (event) => {
                                                            // Triggers select only when user click on the cell
                                                            if (event.target.localName === 'td') {
                                                                document.querySelector(`#${CHECKBOX_NAME}${index}`).dispatchEvent(new MouseEvent('click'));
                                                            }
                                                        }
                                                    },
                                                    m('td',
                                                        m('input', {
                                                            id: `${CHECKBOX_NAME}${index}`,
                                                            type: 'checkbox',
                                                            tabindex: -1,
                                                            name: data.editableYN && CHECKBOX_NAME,
                                                            disabled: !data.editableYN,
                                                            onchange: (event) => { selectRow(event.target.checked, data); }
                                                        })
                                                    ),
                                                    m('td', m('span.cursor-text', data.weekDisplay)),
                                                    m('td', m('span.cursor-text', data.masterDealer)),
                                                    m('td', m('span.cursor-text', data.ediClientCode)),
                                                    m('td', m('span.cursor-text', data.channelGroup)),
                                                    m('td', m('span.cursor-text', data.siteDisplay)),
                                                    m('td', m('span.cursor-text', data.region)),
                                                    m('td', m('span.cursor-text', data.storeCategory)),
                                                    m('td', m('span.cursor-text', data.marketingName)),
                                                    m('td', m('span.cursor-text', data.modelCode)),
                                                    m('td', m('span.cursor-text', data.color)),
                                                    m('td', m('span.cursor-text', data.memory)),
                                                    m('td', m('span.cursor-text', data.targetWOS)),
                                                    m('td', m('span.cursor-text', data.pastWeeksAverageSO)),
                                                    m('td', m('span.cursor-text', data.inventory)),
                                                    m('td', m('span.cursor-text', data.proposedQty)),
                                                    m('td',
                                                        m(NumberInputComponent, {
                                                            value: data.approvedQty,
                                                            disabled: !data.editableYN,
                                                            onChange: (num) => {
                                                                purchaseOrders[index].approvedQty = num;
                                                                let checkbox = document.querySelector(`#${CHECKBOX_NAME}${index}`);

                                                                if (!checkbox.checked) {
                                                                    checkbox.checked = true;
                                                                    selectRow(checkbox.checked, data);
                                                                }
                                                            }
                                                        })
                                                    ),
                                                    m('td', data.approvedStatusName),
                                                    m('td', data.approvedUserID),
                                                    m('td', data.approvedDate),
                                                    m('td', data.createUserID),
                                                    m('td', data.createDate)
                                                )
                                            )
                                        )
                                    )
                                )
                            ),
                            m('.col-12', m(paginationComponent,
                                {
                                    currentPage: pagination.pageIndex,
                                    pageSize: pagination.pageSize,
                                    totalItems: pagination.totalItems,
                                    onPageChanged: (pageIndex) => {
                                        pagination.pageIndex = pageIndex;
                                        getPurchaseOrders();
                                    },
                                    onPageSizeChanged: (pageIndex, pageSize) => {
                                        pagination.pageSize = pageSize;
                                        getPurchaseOrders();
                                    }
                                }
                            )),
                        )
                    )
                ),

                /* CONFIRMATION DIALOG */
                m(Dialog,
                    {
                        isOpen: isConfirmationDialogOpen,
                        closeOnOutsideClick: false,
                        closeOnEscapeKey: false,
                        title: `${this.getText('LT00000758', 'Selected Approval confirmation')} (${selectedAction ? this.getText('LT00000185', 'Approve') : this.getText('LT00000184', 'Reject')})`,
                        class: 'cui-dialog-xxl',
                        onClose: () => { isConfirmationDialogOpen = false; },
                        content: m('.table-responsive',
                            m('table.table.sticky-column-end',
                                m('thead',
                                    m('tr',
                                        m('th', this.getText('LT00000337', 'Week')),
                                        m('th', this.getText('LT00000321', 'Site Code / Name')),
                                        m('th', this.getText('LT00000748', 'Model Code')),
                                        m('th', this.getText('LT00000147', 'Color')),
                                        m('th', this.getText('LT00000749', 'Memory')),
                                        m('th', this.getText('LT00000753', 'Proposed S/I QTY')),
                                        selectedAction && m('th', this.getText('LT00000754', 'Approved QTY'))
                                    )
                                ),
                                m('tbody',
                                    selectedPurchaseOrders.map((data) => m('tr.align-middle',
                                        m('td', data.weekDisplay),
                                        m('td', data.siteDisplay),
                                        m('td', data.modelCode),
                                        m('td', data.color),
                                        m('td', data.memory),
                                        m(`td${selectedAction ? '' : '.highlight'}`, data.proposedQty),
                                        selectedAction && m('td.highlight', data.approvedQty)
                                    ))
                                )
                            )
                        ),
                        footer: m('row',
                            m('.col-12.d-flex.justify-content-end.gap-2',
                                m('button.btn.btn-primary', {
                                    onclick: () => {
                                        confirmationAction(selectedAction);
                                        isConfirmationDialogOpen = false;
                                    }
                                }, this.getText("", "Confirm")),
                                m('button.btn.btn-white', { onclick: () => { isConfirmationDialogOpen = false; } }, this.getText("", "Close"))
                            )
                        )
                    }
                ),

                /* ACTION ERROR DIALOG */
                m(Dialog,
                    {
                        isOpen: isActionErrorDialogOpen,
                        closeOnOutsideClick: false,
                        closeOnEscapeKey: false,
                        class: 'cui-dialog-xl',
                        title: this.getText('EM00000142', 'Error'),
                        content: m('table.table',
                            m('thead',
                                m('tr',
                                    m('th', this.getText('LT00000337', 'Week')),
                                    m('th', this.getText('LT00000723', 'Site Code')),
                                    m('th', this.getText('LT00000748', 'Model Code')),
                                    m('th', this.getText('EM00000146', 'Message')),
                                )
                            ),
                            m('tbody',
                                unsuccessfulPurchaseOrders.map((data, index) => m('tr.align-middle',
                                    m('td', data.weekDisplay),
                                    m('td', data.channelCode),
                                    m('td', data.modelCode),
                                    m('td.word-wrap-break-word', data.message)
                                ))
                            )
                        ),
                        footer: m('row',
                            m('.col-12',
                                m('button.btn.btn-white.float-right', { type: 'button', onclick: () => { isActionErrorDialogOpen = false; } }, this.getText("LT00000021", "Close"))
                            )
                        )
                    }
                ),

                /* EXCEL UPLOAD DIALOG */
                m(PurchaseOrderProposalExcelUploadDialog,
                    {
                        filters,
                        isOpen: isExcelUploadOpen,
                        onClose: () => { isExcelUploadOpen = false; }
                    }
                ),

                this.getBaseViews()
            ];
        }
    };
}
